import React, { useEffect, useState } from 'react';
import CollegeHomeSideBar from '../BasicComponents/CollegeHomeSideBar';
import CollegeHeader from '../BasicComponents/CollegeHeader';
import CollegeFooter from '../BasicComponents/CollegeFooter';
import axios from 'axios';
import API_BASE_URL from '../Config/config';
import { useTenant } from '../Context/TenantContext';
import { useCollegeLogo } from '../hooks/useCollegeLogo';
import { resolveImageUrl } from '../utils/imageUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BarChart3, Users, Award, CreditCard,
  ChevronLeft, ChevronRight, ArrowRight,
  Building2, AlertCircle, Pencil, BookOpen,
  TrendingUp, Zap, Upload,
} from 'lucide-react';

// --- Design Tokens ------------------------------------------------------------
const T = {
  border:  '1px solid #E2E8F0',
  radius:  { sm: '8px', md: '12px', lg: '16px', xl: '20px', xxl: '24px', full: '999px' },
  shadow:  {
    xs: '0 1px 3px rgba(0,0,0,0.05)',
    sm: '0 2px 8px rgba(0,0,0,0.07)',
    md: '0 4px 20px rgba(0,0,0,0.08)',
    lg: '0 8px 40px rgba(79, 70, 229,0.12)',
  },
  brand:       'var(--primary-blue, #4F46E5)',
  brandLight:  'rgba(79, 70, 229,0.08)',
  brandBorder: 'rgba(79, 70, 229,0.18)',
  text: {
    heading:  '#0F172A',
    body:     '#334155',
    muted:    '#64748B',
    disabled: '#94A3B8',
  },
  bg: { main: '#F8FAFC', section: '#FFFFFF' },
};

// --- Quick-access tiles -------------------------------------------------------
const QUICK_LINKS = [
  {
    icon: Users,
    label:    'Student Management',
    desc:     'Enrolments, bulk uploads, and student records',
    path:     '/college/student-management',
    accent:   'var(--primary-blue, #4F46E5)',
    accentBg: 'rgba(79, 70, 229,0.07)',
  },
  {
    icon: BarChart3,
    label:    'Analytics',
    desc:     'Skill progress, activity, and placement readiness',
    path:     '/college-analytics',
    accent:   '#7C3AED',
    accentBg: 'rgba(124,58,237,0.07)',
  },
  {
    icon: Award,
    label:    'Certificates',
    desc:     'Issue and manage student achievement certificates',
    path:     '/college/certificates',
    accent:   '#059669',
    accentBg: 'rgba(5,150,105,0.07)',
  },
  {
    icon: Upload,
    label:    'Bulk Upload',
    desc:     'Add multiple students via CSV in one step',
    path:     '/college/student-management',
    state:    { activeTab: 'upload' },
    accent:   '#0891B2',
    accentBg: 'rgba(8,145,178,0.07)',
  },
  {
    icon: TrendingUp,
    label:    'Top Performers',
    desc:     'View leading students across all skill tracks',
    path:     '/college-analytics',
    state:    { activeTab: 'All' },
    accent:   '#D97706',
    accentBg: 'rgba(217,119,6,0.07)',
  },
  {
    icon: CreditCard,
    label:    'Purchase Credits',
    desc:     'Buy student enrolment credits and manage billing',
    path:     '/college/purchase-credits',
    accent:   '#DB2777',
    accentBg: 'rgba(219,39,119,0.07)',
  },
];

// --- Stat card ----------------------------------------------------------------
const StatCard = ({ icon: Icon, label, value, accent, accentBg, loading }) => (
  <div style={{
    background: T.bg.section, border: T.border, borderRadius: T.radius.xl,
    padding: '20px 22px', boxShadow: T.shadow.xs,
    display: 'flex', alignItems: 'center', gap: '16px',
  }}>
    <div style={{
      width: '44px', height: '44px', borderRadius: T.radius.lg,
      background: accentBg, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon style={{ width: '20px', height: '20px', color: accent }} />
    </div>
    <div style={{ minWidth: 0 }}>
      <p style={{ fontSize: '11px', fontWeight: 700, color: T.text.muted, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 4px' }}>
        {label}
      </p>
      {loading ? (
        <div style={{ width: '48px', height: '20px', borderRadius: T.radius.sm, background: '#E2E8F0', animation: 'pulse 1.4s ease-in-out infinite' }} />
      ) : (
        <p style={{ fontSize: '22px', fontWeight: 800, color: T.text.heading, margin: 0, letterSpacing: '-0.02em', lineHeight: 1 }}>
          {value ?? '-'}
        </p>
      )}
    </div>
  </div>
);

// --- CollegeUI (College Home Page) -------------------------------------------
const CollegeUI = () => {
  const [collegeData,  setCollegeData]  = useState(null);
  const [credits,      setCredits]      = useState(null);
  const [studentCount, setStudentCount] = useState(null);
  const [certificateCount, setCertificateCount] = useState(null);
  const [slideIdx,     setSlideIdx]     = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error,        setError]        = useState('');

  const { getLogoUrl, getCollegeName, tenant } = useTenant();
  const { logo: collegeLogo } = useCollegeLogo();
  const navigate = useNavigate();
  const location = useLocation();

  const user        = JSON.parse(localStorage.getItem('user') || '{}');
  const username    = user?.username || user?.userId;
  useEffect(() => {
    const fetchCollege = async () => {
      try {
        if (!username) { setError('User session not found. Please log in again.'); setLoading(false); return; }
        const res = await axios.get(`${API_BASE_URL}/colleges/username/${username}`);
        setCollegeData(res.data);
      } catch (err) {
        console.error('Failed to load college data:', err);
        setError('Unable to load college data. Please refresh.');
      } finally {
        setLoading(false);
      }
    };
    fetchCollege();
  }, []);

  // -- Fetch stats (credits + student count + certificate count) ---------------------------------
  useEffect(() => {
    if (!collegeData) return;
    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const college_id = collegeData.id || collegeData._id || collegeData.college_id;
        const [creditsRes, studentsRes, certificatesRes] = await Promise.allSettled([
          axios.post(`${API_BASE_URL}/credits/balance`, { college_id }),
          axios.post(`${API_BASE_URL}/students/list`, { college_id, limit: 5000 }),
          axios.get(`${API_BASE_URL}/api/certificates/college/${college_id}?limit=1`),
        ]);
        if (creditsRes.status === 'fulfilled') {
          const d   = creditsRes.value.data;
          const raw = d?.credits ?? d?.balance ?? d?.data?.credits ?? d?.data?.balance;
          setCredits(
            typeof raw === 'number' ? raw :
            typeof raw === 'object' && raw !== null
              ? (raw.balance ?? raw.available ?? raw.total ?? raw.credits ?? null)
              : (raw !== undefined ? Number(raw) : null)
          );
        }
        if (studentsRes.status === 'fulfilled') {
          const sd = studentsRes.value.data;
          setStudentCount(sd?.total ?? (Array.isArray(sd?.students) ? sd.students.length : null));
        }

        if (certificatesRes.status === 'fulfilled') {
          const d = certificatesRes.value.data;
          setCertificateCount(d?.pagination?.total ?? d?.total ?? d?.data?.length ?? null);
        }
      } catch { /* stats are non-critical */ }
      finally { setStatsLoading(false); }
    };
    fetchStats();
  }, [collegeData]);

  // -- Parse campus images ---------------------------------------------------
  // Backend normalises to a JS array, but guard against the raw PostgreSQL
  // array literal "{url1,url2}" in case an older API version is deployed.
  const parseImages = (raw) => {
    let urls = [];
    if (!raw) return urls;
    if (Array.isArray(raw)) {
      urls = raw.filter(u => u && typeof u === 'string');
    } else if (typeof raw === 'string') {
      const s = raw.trim();
      if (s.startsWith('{') && s.endsWith('}')) {
        urls = s.slice(1, -1).split(',').map(u => u.trim().replace(/^"|"$/g, '')).filter(Boolean);
      } else {
        try { const p = JSON.parse(s); urls = Array.isArray(p) ? p.filter(u => u && typeof u === 'string') : []; } catch {}
      }
    }
    return urls.filter(u => u && typeof u === 'string');
  };
  const images = parseImages(collegeData?.images);

  // -- Auto-advance slideshow ------------------------------------------------
  useEffect(() => {
    if (images.length <= 1) return;
    const id = setInterval(() => setSlideIdx((p) => (p + 1) % images.length), 6500);
    return () => clearInterval(id);
  }, [images.length]);

  // -- Resolved values -------------------------------------------------------
  const collegeName = tenant?.collegeName || tenant?.college_name || collegeData?.name || getCollegeName() || 'Institution Portal';
  const hour        = new Date().getHours();
  const greeting    = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const adminName   = user?.username || user?.userId || 'Administrator';

  // -- Resolve logo ----------------------------------------------------------
  const logoPath = collegeLogo;

  // -- Loading ---------------------------------------------------------------
  if (loading) return (
    <div className="college-main-layout" style={{ background: T.bg.main }}>
      <CollegeHeader />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
        <div style={{ width: '20px', height: '20px', border: `2px solid ${T.brand}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <p style={{ fontSize: '14px', color: T.text.muted, fontWeight: 500 }}>Loading institution data...</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  // -- Error -----------------------------------------------------------------
  if (error) return (
    <div className="college-main-layout" style={{ background: T.bg.main }}>
      <CollegeHeader />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ textAlign: 'center', maxWidth: '360px' }}>
          <div style={{ width: '52px', height: '52px', borderRadius: T.radius.lg, background: '#FEF2F2', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <AlertCircle style={{ width: '22px', height: '22px', color: '#DC2626' }} />
          </div>
          <p style={{ fontWeight: 700, color: T.text.heading, fontSize: '15px', margin: '0 0 6px' }}>Something went wrong</p>
          <p style={{ fontSize: '13px', color: T.text.muted, margin: 0 }}>{error}</p>
        </div>
      </div>
    </div>
  );

  // -- Main Render -----------------------------------------------------------
  return (
    <div className="college-main-layout" style={{ background: T.bg.main, fontFamily: 'inherit' }}>
      <CollegeHeader />

      <div className="college-content-wrapper">
        <CollegeHomeSideBar />

        <main className={`college-main-content ${location.pathname !== `/collegehome/${username}` ? 'w-full' : ''}`}>
          <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '32px 28px 80px' }}>

            {/* -- Hero Banner ---------------------------------------------- */}
            <div style={{
              background: T.bg.section, border: T.border, borderRadius: T.radius.xxl,
              overflow: 'hidden', boxShadow: T.shadow.md, marginBottom: '20px',
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute', top: '-60px', right: '-60px',
                width: '280px', height: '280px', borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(79, 70, 229,0.05) 0%, transparent 70%)',
                pointerEvents: 'none',
              }} />
              <div style={{ padding: '28px 32px', display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap', position: 'relative' }}>
                {logoPath && (
                  <div style={{
                    width: '72px', height: '72px', borderRadius: T.radius.xl,
                    background: T.bg.main, border: `2px solid ${T.brandBorder}`,
                    overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, boxShadow: T.shadow.sm,
                  }}>
                    <img
                      src={logoPath} alt={collegeName}
                      style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '6px' }}
                      onError={(e) => { if (e.target.src !== window.location.origin + '/logo192.png') { e.target.onerror = null; e.target.src = '/logo192.png'; } }}
                    />
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
                  <h1 style={{ fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)', fontWeight: 900, color: T.text.heading, letterSpacing: '-0.02em', lineHeight: 1.2, margin: '0 0 4px' }}>
                    {collegeName}
                  </h1>
                  <p style={{ fontSize: '12px', fontWeight: 500, color: T.text.muted, margin: '0 0 8px', letterSpacing: '0.01em' }}>
                    {greeting}, <span style={{ fontWeight: 700, color: T.text.body }}>{adminName}</span>
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center',
                      padding: '3px 10px', borderRadius: T.radius.full,
                      background: T.brandLight, border: `1px solid ${T.brandBorder}`,
                      fontSize: '10px', fontWeight: 700, color: T.brand,
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                    }}>
                      Admin Portal · The Skill Genie
                    </span>
                    {collegeData?.state && (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        padding: '3px 10px', borderRadius: T.radius.full,
                        background: '#F1F5F9', border: T.border,
                        fontSize: '10px', fontWeight: 600, color: T.text.muted,
                      }}>
                        {[collegeData.state, collegeData.country].filter(Boolean).join(', ')}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', flexShrink: 0, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => navigate('/CollegeEditProfile')}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '9px 16px', borderRadius: T.radius.md,
                      background: T.bg.main, color: T.text.body, border: T.border,
                      cursor: 'pointer', fontSize: '13px', fontWeight: 600, transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#F1F5F9'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = T.bg.main; }}
                  >
                    <Pencil style={{ width: '13px', height: '13px' }} />
                    Edit Profile
                  </button>
                  <button
                    onClick={() => navigate('/college-analytics')}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '9px 18px', borderRadius: T.radius.md,
                      background: T.brand, color: '#FFFFFF', border: 'none',
                      cursor: 'pointer', fontSize: '13px', fontWeight: 700,
                      boxShadow: '0 3px 12px rgba(79, 70, 229,0.28)', transition: 'opacity 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.88'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                  >
                    <BarChart3 style={{ width: '13px', height: '13px' }} />
                    Analytics
                  </button>
                </div>
              </div>
            </div>

            {/* ── Stat Cards ───────────────────────────────────────────────── */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '14px', marginBottom: '20px',
            }}>
              <StatCard icon={Users}     label="Total Students"    value={studentCount !== null ? studentCount.toLocaleString() : '-'} accent="var(--primary-blue, #4F46E5)" accentBg="rgba(79, 70, 229,0.08)"  loading={statsLoading} />
              <StatCard icon={CreditCard} label="Credits Available" value={credits !== null ? credits.toLocaleString() : '-'}          accent="#D97706" accentBg="rgba(217,119,6,0.08)"   loading={statsLoading} />
              <StatCard icon={Award}     label="Certificates Issued" value={certificateCount !== null ? certificateCount.toLocaleString() : (collegeData?.certificates_issued ?? '-')} accent="#059669" accentBg="rgba(5,150,105,0.08)"  loading={statsLoading} />
              <StatCard icon={Zap}       label="Platform Status"    value="Active"                                                      accent="#7C3AED" accentBg="rgba(124,58,237,0.08)" loading={false} />
            </div>

            {/* ── Quick Actions ─────────────────────────────────────────────── */}
            <div style={{
              background: T.bg.section, border: T.border, borderRadius: T.radius.xxl,
              boxShadow: T.shadow.xs, overflow: 'hidden', marginBottom: '20px',
            }}>
              <div style={{ padding: '20px 24px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                  <Zap style={{ width: '14px', height: '14px', color: T.brand }} />
                  <p style={{ fontSize: '11px', fontWeight: 700, color: T.text.muted, textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
                    Quick Actions
                  </p>
                </div>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '1px', background: '#E2E8F0', borderTop: T.border,
              }}>
                {QUICK_LINKS.map((link) => {
                  const Icon = link.icon;
                  return (
                    <button
                      key={link.label}
                      onClick={() => navigate(link.path, { state: link.state })}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: '14px',
                        padding: '20px 22px', background: T.bg.section,
                        border: 'none', cursor: 'pointer', textAlign: 'left',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = T.bg.main; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = T.bg.section; }}
                    >
                      <div style={{
                        width: '38px', height: '38px', borderRadius: T.radius.md,
                        background: link.accentBg, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon style={{ width: '18px', height: '18px', color: link.accent }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '3px' }}>
                          <p style={{ fontSize: '13px', fontWeight: 700, color: T.text.heading, margin: 0 }}>{link.label}</p>
                          <ArrowRight style={{ width: '12px', height: '12px', color: T.text.disabled, flexShrink: 0 }} />
                        </div>
                        <p style={{ fontSize: '11px', color: T.text.muted, margin: 0, fontWeight: 500, lineHeight: 1.5 }}>
                          {link.desc}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Campus Showcase ───────────────────────────────────────────── */}
            <div style={{
              background: T.bg.section, border: T.border, borderRadius: T.radius.xxl,
              overflow: 'hidden', boxShadow: T.shadow.xs,
            }}>
              <div style={{
                padding: '20px 28px 16px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderBottom: T.border,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Building2 style={{ width: '14px', height: '14px', color: T.brand }} />
                  <p style={{ fontSize: '11px', fontWeight: 700, color: T.text.muted, textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
                    Campus Showcase
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={() => setSlideIdx((i) => (i - 1 + images.length) % images.length)}
                        style={{ width: '30px', height: '30px', borderRadius: T.radius.md, background: T.bg.main, border: T.border, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: T.text.muted, transition: 'all 0.15s' }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.brand; e.currentTarget.style.color = T.brand; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.color = T.text.muted; }}
                      >
                        <ChevronLeft style={{ width: '14px', height: '14px' }} />
                      </button>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: T.text.muted, minWidth: '36px', textAlign: 'center' }}>
                        {slideIdx + 1} / {images.length}
                      </span>
                      <button
                        onClick={() => setSlideIdx((i) => (i + 1) % images.length)}
                        style={{ width: '30px', height: '30px', borderRadius: T.radius.md, background: T.bg.main, border: T.border, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: T.text.muted, transition: 'all 0.15s' }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.brand; e.currentTarget.style.color = T.brand; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.color = T.text.muted; }}
                      >
                        <ChevronRight style={{ width: '14px', height: '14px' }} />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => navigate('/CollegeEditProfile')}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                      padding: '6px 12px', borderRadius: T.radius.md,
                      background: T.brandLight, border: `1px solid ${T.brandBorder}`,
                      color: T.brand, cursor: 'pointer', fontSize: '11px', fontWeight: 700, transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(79, 70, 229,0.13)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = T.brandLight; }}
                  >
                    <Pencil style={{ width: '11px', height: '11px' }} />
                    Manage Photos
                  </button>
                </div>
              </div>

              <div style={{ padding: '20px 24px 24px' }}>
                {images.length > 0 ? (
                  <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                    <div style={{
                      flex: '1 1 0', borderRadius: T.radius.xl,
                      overflow: 'hidden', border: T.border,
                      boxShadow: T.shadow.sm, position: 'relative', minWidth: 0,
                    }}>
                      <img
                        key={slideIdx}
                        src={resolveImageUrl(images[slideIdx])}
                        alt={`Campus view ${slideIdx + 1}`}
                        style={{ width: '100%', height: 'clamp(220px, 36vw, 360px)', objectFit: 'cover', display: 'block', animation: 'fadeIn 0.35s ease' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div style={{ position: 'absolute', insetX: 0, bottom: 0, height: '90px', background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)', pointerEvents: 'none' }} />
                      <div style={{ position: 'absolute', bottom: '16px', left: '20px', pointerEvents: 'none' }}>
                        <p style={{ fontSize: '14px', fontWeight: 800, color: '#FFFFFF', margin: 0, textShadow: '0 1px 6px rgba(0,0,0,0.4)' }}>{collegeName}</p>
                        <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.72)', margin: '2px 0 0', fontWeight: 500 }}>Campus &middot; Skill Genie</p>
                      </div>
                      {images.length > 1 && (
                        <div style={{ position: 'absolute', bottom: '14px', right: '16px', display: 'flex', gap: '5px' }}>
                          {images.map((_, i) => (
                            <button
                              key={i}
                              onClick={() => setSlideIdx(i)}
                              style={{ border: 'none', padding: 0, cursor: 'pointer', borderRadius: T.radius.full, background: '#FFFFFF', opacity: i === slideIdx ? 1 : 0.45, width: i === slideIdx ? '18px' : '6px', height: '6px', transition: 'all 0.25s' }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    {images.length > 1 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0, width: '84px', maxHeight: 'clamp(220px, 36vw, 360px)', overflowY: 'auto' }}>
                        {images.map((img, i) => (
                          <button
                            key={i}
                            onClick={() => setSlideIdx(i)}
                            style={{ padding: 0, border: 'none', cursor: 'pointer', borderRadius: T.radius.md, overflow: 'hidden', flexShrink: 0, outline: i === slideIdx ? `2px solid ${T.brand}` : '2px solid transparent', outlineOffset: '2px', transition: 'outline 0.15s' }}
                          >
                            <img src={img} alt={`Thumb ${i + 1}`} style={{ width: '84px', height: '58px', objectFit: 'cover', display: 'block', opacity: i === slideIdx ? 1 : 0.55, transition: 'opacity 0.15s' }} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ height: '240px', borderRadius: T.radius.xl, background: T.bg.main, border: '1.5px dashed #CBD5E1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                    <Building2 size={32} style={{ opacity: 0.35 }} />
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontSize: '14px', fontWeight: 600, color: T.text.muted, margin: '0 0 4px' }}>No campus photos yet</p>
                      <p style={{ fontSize: '12px', color: T.text.disabled, margin: '0 0 16px' }}>Add images to showcase your campus to students.</p>
                      <button
                        onClick={() => navigate('/CollegeEditProfile')}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 18px', borderRadius: T.radius.md, background: T.brandLight, border: `1px solid ${T.brandBorder}`, color: T.brand, cursor: 'pointer', fontSize: '12px', fontWeight: 700 }}
                      >
                        <BookOpen style={{ width: '13px', height: '13px' }} />
                        Add Campus Photos
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </main>
      </div>

      <CollegeFooter />

      <style>{`
        footer[aria-label*="college Portal footer"] {
           transition: margin-left 300ms ease;
        }
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes fadeIn  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes pulse   { 0%,100% { opacity: 1; } 50% { opacity: 0.45; } }
      `}</style>
    </div>
  );
};

export default CollegeUI;